// approvalQueues.ts
//
// What is waiting for the signed-in person on the Approval Desk: each queue's
// read, and the one rule for the desk's total -- the figure the sidebar shows
// beside "Approval Desk". The desk (ApprovalsDesk.tsx) reads its queues through
// these functions and publishes its own total, so the sidebar and the desk's
// cards count the same things the same way.
//
// The total counts the approval queues the desk's cards count: expenses (mine),
// timesheets, leave, client invoices, PSV reports (PSV approvers only),
// purchase orders, estimates and variations. Training and "due to receipt" are
// on the desk but are not approvals, so they are not in it.

import { useEffect, useState } from 'react';
import axios from 'axios';
import { apiUrl } from '../../config/apiConfig';
import { useUserRoles } from '../shared/PageTabs';
import { canApprovePsvReports, isTimesheetAdmin } from '../../utils/permissions';
import { ageDays } from './age';

/* --------------------------------- Types ---------------------------------- */
export interface RawDataItem {
  _id?: string;
  id?: string;
  estId?: string;
  invoiceId?: string;
  requestId?: string;
  estNumber?: string;
  number?: string | number;
  proReqNumber?: string;
  requestNumber?: string;
  docRefNo?: string;
  invoiceNumber?: string;
  title?: string;
  subject?: string;
  activityName?: string;
  projectName?: string;
  description?: string;
  status?: string;
  statusDisplay?: string;
  approvalsStatus?: string;
  createdAt?: string | { $date: string };
  created_on?: string;
  created?: string;
  date?: string;
  client?: string | { name?: string };
  reference?: string;
  reqNo?: string;
  reportNo?: string;
  general?: { clientName?: string };
  name?: string;
  code?: string;
  email?: string;
  label?: string;
  value?: string;
  [key: string]: unknown;
}

export interface NormalizedRow {
  kind: string;
  id: string;
  ref: string;
  title: string;
  status: string;
  createdAt: string;
  raw: RawDataItem;
}

export interface ApprovalStats {
  pending: number;
  urgent: number;
  soon: number;
  items: ApprovalItem[];
  /** Not read yet. Only the queues the desk lists itself track this. */
  loading?: boolean;
  /** The read failed, so an empty list is not "nothing waiting". */
  failed?: boolean;
}

export interface ApprovalItem {
  id: string;
  label: string;
  subtitle: string;
  submittedAt: string;
}

export const NO_STATS: ApprovalStats = { pending: 0, urgent: 0, soon: 0, items: [] };

/** A queue's stats from its items, aged by when each was submitted. */
export function statsOf(items: ApprovalItem[]): ApprovalStats {
  let urgent = 0;
  let soon = 0;
  for (const item of items) {
    const age = ageDays(item.submittedAt);
    if (age >= 3) urgent++;
    else if (age >= 1) soon++;
  }
  return { pending: items.length, urgent, soon, items };
}

/** Prefer global axios Authorization; else prop token; else localStorage.user.token */
export function getAuthHeaders(propToken?: string): Record<string, string> {
  const auth = axios?.defaults?.headers?.common?.Authorization as string | undefined;
  if (auth) return { Authorization: auth };
  if (propToken) return { Authorization: `Bearer ${propToken}` };
  try {
    const raw = localStorage.getItem('user');
    if (raw) {
      const { token } = JSON.parse(raw) as { token?: string };
      if (token) return { Authorization: `Bearer ${token}` };
    }
  } catch {
    // Ignore parsing errors
  }
  return {};
}

/** Coerce any value to a short display string (prevents object-as-child crashes) */
function textOf(...vals: unknown[]): string {
  for (const v of vals) {
    if (v === null || v === undefined) continue;
    if (typeof v === 'string' || typeof v === 'number') return String(v);
    if (Array.isArray(v)) {
      return v
        .map(x =>
          typeof x === 'object' && x !== null
            ? textOf(
                (x as Record<string, unknown>).name,
                (x as Record<string, unknown>).title,
                (x as Record<string, unknown>).code,
                (x as Record<string, unknown>).number,
                (x as Record<string, unknown>).id,
                (x as Record<string, unknown>).email
              )
            : x
        )
        .join(', ');
    }
    if (typeof v === 'object') {
      const obj = v as Record<string, unknown>;
      const pick =
        obj.name ??
        obj.title ??
        obj.code ??
        obj.number ??
        obj.id ??
        obj.label ??
        obj.value ??
        obj.email;
      if (pick !== undefined) return String(pick);
      try {
        return JSON.stringify(v);
      } catch {
        return '';
      }
    }
  }
  return '';
}

/** An expense approval row. */
function normalizeExpenseRow(raw: RawDataItem): NormalizedRow {
  const id = raw?._id || raw?.id || '';
  const title =
    textOf(raw?.title, raw?.subject, raw?.activityName, raw?.projectName, raw?.description) || '';
  const status =
    textOf(raw?.statusDisplay) || textOf(raw?.status) || textOf(raw?.approvalsStatus) || '—';
  // createdAt across shapes (fallback to "date" for expenses)
  const createdAtRaw =
    typeof raw?.createdAt === 'object' && raw?.createdAt !== null && '$date' in raw.createdAt
      ? (raw.createdAt as { $date: string }).$date
      : raw?.createdAt || raw?.created_on || raw?.created || raw?.date;
  const createdAt = createdAtRaw ? String(createdAtRaw) : '';
  const ref = textOf(raw?.description, title) || (id ? `Expense #${id}` : 'Expense');
  return { kind: 'expenses', id, ref, title, status, createdAt, raw };
}

/**
 * An expense still waiting on this user (their own turn, not decided). The
 * Expenses list's Mine / Current goes by it too, so the list and the count
 * agree.
 */
export function isMyPendingExpense(raw: { status?: unknown; statusDisplay?: unknown; myApprovalStatus?: unknown }): boolean {
  const status = String(raw?.status || raw?.statusDisplay || '').toLowerCase();
  if (['approved', 'closed', 'paid', 'completed', 'ordered', 'rejected'].some(s => status.includes(s))) {
    return false;
  }
  return String(raw?.myApprovalStatus || '').toLowerCase() === 'pending';
}

/** The "mine" expense rows the desk lists and counts. */
export function myPendingExpenses(list: RawDataItem[]): NormalizedRow[] {
  return (list || [])
    .filter(isMyPendingExpense)
    .map(normalizeExpenseRow)
    .filter(r => r.id);
}

// Approvals queue (delegation-aware on server)
const EXPENSE_APPROVALS_URL = apiUrl('/api/expenses/approvals');

/* --------------------------------- Queues --------------------------------- */
interface QueueArgs {
  token?: string;
}

export async function fetchExpenseQueue({
  token,
  actingAsUserId = '',
}: QueueArgs & { actingAsUserId?: string }): Promise<{
  mine: RawDataItem[];
  allTotal: number;
  failed?: boolean;
}> {
  // "Mine" = my delegation-aware pending approvals (direct + on-behalf-of).
  // "All Approvals" = every pending approval (server returns all only for
  // admins; for non-admins scope=all collapses to their own set) -- the same
  // question the Expenses list asks under All / Current. Both uncapped, as the
  // list is, so a count never stops at the server's default 500.
  // When "acting as" a specific person, both queries scope to ONLY that
  // person's queue (the server enforces this), so numbers match the delegator
  // exactly — never a combined or org-wide total.
  const actingParam = actingAsUserId ? `&actingAsUserId=${encodeURIComponent(actingAsUserId)}` : '';
  try {
    const [mineRes, allRes] = await Promise.all([
      axios.get(`${EXPENSE_APPROVALS_URL}?mine=waiting&status=pending&scope=mine&limit=all${actingParam}`, {
        headers: getAuthHeaders(token),
      }),
      axios.get(`${EXPENSE_APPROVALS_URL}?mine=all&status=pending&scope=all&limit=all${actingParam}`, {
        headers: getAuthHeaders(token),
      }),
    ]);
    const list = Array.isArray(mineRes.data?.items) ? mineRes.data.items : [];
    const allTotal = Array.isArray(allRes.data?.items) ? allRes.data.items.length : list.length;
    return { mine: list, allTotal };
  } catch {
    return { mine: [], allTotal: 0, failed: true };
  }
}

// Fetch timesheet approvals for KPI + action items.
// Mirrors the param convention used by the embedded <TimesheetApprovals>
// (TimesheetApprovals.tsx fetchTimesheets) so the badge count and the
// list it links to always reflect the same backend query:
//   - Admin + adminMode=true  → omit param (backend uses admin view)
//   - Admin + adminMode=false → adminMode=false (approver/delegate filter)
//   - Non-admin               → omit param (backend ignores it; uses approver/delegate filter)
// Also threads `actingAsUserId` through so a delegated approver sees the
// same scope on the dashboard as on the standalone page.
export async function fetchTimesheetQueue({
  token,
  userRoles,
  adminMode = false,
  actingAsUserId = '',
}: QueueArgs & { userRoles: string[]; adminMode?: boolean; actingAsUserId?: string }): Promise<ApprovalStats> {
  try {
    const isAdminUser = isTimesheetAdmin(userRoles);
    const params: Record<string, string> = {};
    if (isAdminUser && !adminMode) params.adminMode = 'false';
    if (actingAsUserId) params.actingAsUserId = actingAsUserId;
    const res = await axios.get('/api/timesheets/approvals/queue', {
      headers: getAuthHeaders(token),
      params,
    });
    const allItems = Array.isArray(res.data?.data) ? res.data.data : [];
    // Drop weeks with nothing the approver can action yet.
    //
    // A timesheet whose lines are ALL future days cannot be approved, so
    // counting it here overstates the badge and sends people to a screen
    // with nothing to do. A week with SOME worked days stays — those lines
    // are actionable.
    //
    // `approvalCutoff` is today-in-Brisbane as the SERVER computed it; it is
    // never derived from the browser clock, or this count would disagree
    // with the backend that enforces it.
    const approvalCutoff: string | null = res.data?.meta?.approvalCutoff ?? null;
    const rawItems = approvalCutoff
      ? allItems.filter((item: any) => {
          const lines = Array.isArray(item?.lines) ? item.lines : [];
          if (lines.length === 0) return true;
          return lines.some(
            (l: any) => !l?.date || new Date(l.date).toISOString().slice(0, 10) <= approvalCutoff,
          );
        })
      : allItems;
    let urgent = 0;
    let soon = 0;
    const mapped: ApprovalItem[] = rawItems.map((item: any) => {
      const age = ageDays(item.submittedAt || '');
      if (age >= 3) urgent++;
      else if (age >= 1) soon++;
      const we = item.weekEndingDate ? new Date(item.weekEndingDate).toLocaleDateString('en-AU') : '';
      return {
        id: item._id,
        label: item.employeeName || 'Timesheet',
        subtitle: we ? `W/E ${we}` : '',
        submittedAt: item.submittedAt || '',
      };
    });
    return { pending: mapped.length, urgent, soon, items: mapped };
  } catch {
    return { pending: 0, urgent: 0, soon: 0, items: [], failed: true };
  }
}

// Fetch leave approvals for KPI + action items
export async function fetchLeaveQueue({ token }: QueueArgs): Promise<ApprovalStats> {
  try {
    const res = await axios.get('/api/leave/approvals', {
      headers: getAuthHeaders(token),
    });
    const rawItems: any[] = Array.isArray(res.data?.data) ? res.data.data : [];
    let urgent = 0;
    let soon = 0;
    const countAge = (dateField: string) => {
      const age = ageDays(dateField);
      if (age >= 3) urgent++;
      else if (age >= 1) soon++;
    };
    const mapped: ApprovalItem[] = rawItems.map((item: any) => {
      const dateField = item.submittedAt || item.createdAt || item.dateFrom || '';
      countAge(dateField);
      const from = item.dateFrom ? new Date(item.dateFrom).toLocaleDateString('en-AU') : '';
      const to = item.dateTo ? new Date(item.dateTo).toLocaleDateString('en-AU') : '';
      return {
        id: item._id,
        label: item.personName || 'Leave Request',
        subtitle: `${item.leaveTypeDescription || 'Leave'}${from ? ` — ${from}` : ''}${to ? ` to ${to}` : ''}`,
        submittedAt: dateField,
      };
    });
    return { pending: mapped.length, urgent, soon, items: mapped };
  } catch {
    return { pending: 0, urgent: 0, soon: 0, items: [], failed: true };
  }
}

// Fetch invoice approvals for KPI + action items
export async function fetchInvoiceQueue({ token }: QueueArgs): Promise<ApprovalStats> {
  try {
    const res = await axios.get('/api/client-invoices?myApprovals=true', {
      headers: getAuthHeaders(token),
    });
    const rawItems = Array.isArray(res.data) ? res.data : [];
    let urgent = 0;
    let soon = 0;
    const mapped: ApprovalItem[] = rawItems.map((item: any) => {
      const age = ageDays(item.createdDate || '');
      if (age >= 3) urgent++;
      else if (age >= 1) soon++;
      return {
        id: item._id,
        label: item.invoiceNumber || item.reference || 'Invoice',
        subtitle: item.clientName || item.jobName || '',
        submittedAt: item.createdDate || '',
      };
    });
    return { pending: mapped.length, urgent, soon, items: mapped };
  } catch {
    return { pending: 0, urgent: 0, soon: 0, items: [], failed: true };
  }
}

/**
 * Pending Purchase Order approvals — server returns every PO where the
 * current user has at least one pending line-level approval (or is a
 * delegate). We treat each PO as a single KPI item; the
 * PurchaseOrderApprovals page handles the per-line drilldown.
 */
export async function fetchPoQueue({ token }: QueueArgs): Promise<ApprovalStats> {
  try {
    const res = await axios.get('/api/purchase-orders?scope=approvals', {
      headers: getAuthHeaders(token),
    });
    const rawItems = Array.isArray(res.data) ? res.data : Array.isArray(res.data?.items) ? res.data.items : [];
    let urgent = 0;
    let soon = 0;
    const mapped: ApprovalItem[] = rawItems.map((item: any) => {
      const age = ageDays(item.createdAt || item.submittedAt || '');
      if (age >= 3) urgent++;
      else if (age >= 1) soon++;
      return {
        id: item._id,
        label: item.reference || 'PO',
        subtitle: item.vendor?.name || item.vendor?.code || item.standardText || '',
        submittedAt: item.createdAt || item.submittedAt || '',
      };
    });
    return { pending: mapped.length, urgent, soon, items: mapped };
  } catch {
    return { pending: 0, urgent: 0, soon: 0, items: [], failed: true };
  }
}

/**
 * Estimates naming this user as an undecided site or client approver
 * (GET /api/estimates/awaiting-me), in full.
 */
export async function fetchEstimateQueue({ token }: QueueArgs): Promise<ApprovalStats> {
  try {
    const res = await axios.get('/api/estimates/awaiting-me', { headers: getAuthHeaders(token) });
    const rawItems: any[] = Array.isArray(res.data) ? res.data : [];
    return statsOf(
      rawItems.map(item => ({
        id: item._id,
        label: `Estimate ${item.estNumber || item.estimateNumber || ''}`.trim(),
        subtitle: [item.project?.name || item.project?.number, item.supplier]
          .filter(Boolean)
          .join(' · '),
        submittedAt: item.createdAt || '',
      }))
    );
  } catch {
    return { ...NO_STATS, failed: true };
  }
}

/**
 * Variations naming this user as the job's JM or SM at submit, or a
 * delegate of one (GET /api/variations/awaiting-me), in full. The item id
 * carries the proposal, where the variation is decided.
 */
export async function fetchVariationQueue({ token }: QueueArgs): Promise<ApprovalStats> {
  try {
    const res = await axios.get('/api/variations/awaiting-me', { headers: getAuthHeaders(token) });
    const rawItems: any[] = Array.isArray(res.data?.data) ? res.data.data : [];
    return statsOf(
      rawItems.map(item => ({
        id: `${item.proposalId}:${item._id}`,
        label: `${item.jobCode || ''} ${item.variationNumber || ''} — ${item.title || ''}`.trim(),
        subtitle: [item.proposalName, item.estimatedSales ? `$${Number(item.estimatedSales).toLocaleString('en-AU', { maximumFractionDigits: 0 })}` : '', item.submittedBy]
          .filter(Boolean)
          .join(' · '),
        submittedAt: item.submittedAt || '',
      }))
    );
  } catch {
    return { ...NO_STATS, failed: true };
  }
}

/** Every PSV report this user may approve now (GET /api/psv-reports/awaiting-approval). */
export async function fetchPsvQueue({
  token,
  canApprovePsv,
}: QueueArgs & { canApprovePsv: boolean }): Promise<ApprovalStats> {
  if (!canApprovePsv) return NO_STATS;
  try {
    const res = await axios.get('/api/psv-reports/awaiting-approval', { headers: getAuthHeaders(token) });
    const rawItems: any[] = Array.isArray(res.data?.data) ? res.data.data : [];
    return statsOf(
      rawItems.map(item => ({
        id: item._id,
        label: `PSV ${item.reportNo || ''}`.trim(),
        subtitle: [item.clientName, item.plantName, item.tagNo || item.serialNo, item.createdBy]
          .filter(Boolean)
          .join(' · '),
        submittedAt: item.submittedAt || '',
      }))
    );
  } catch {
    return { ...NO_STATS, failed: true };
  }
}

/* --------------------------------- Total ---------------------------------- */

/**
 * The desk's total: "mine" expenses plus every other approval queue's pending.
 * Null when any read failed — a partial total is a wrong number, not a count.
 */
export function deskApprovalTotal(
  expenses: { count: number; failed?: boolean },
  queues: ApprovalStats[]
): number | null {
  if (expenses.failed || queues.some(q => q.failed || q.loading)) return null;
  return queues.reduce((sum, q) => sum + q.pending, expenses.count);
}

/**
 * The desk's total in the scope it opens on (nobody acted as, timesheet admin
 * mode off). Rejects when any queue could not be read.
 */
export async function countDeskApprovals({
  token,
  userRoles,
}: {
  token?: string;
  userRoles: string[];
}): Promise<number> {
  const [expenses, ...queues] = await Promise.all([
    fetchExpenseQueue({ token }),
    fetchTimesheetQueue({ token, userRoles }),
    fetchLeaveQueue({ token }),
    fetchInvoiceQueue({ token }),
    fetchPsvQueue({ token, canApprovePsv: canApprovePsvReports(userRoles) }),
    fetchPoQueue({ token }),
    fetchEstimateQueue({ token }),
    fetchVariationQueue({ token }),
  ] as const);
  const total = deskApprovalTotal(
    { count: myPendingExpenses(expenses.mine).length, failed: expenses.failed },
    queues as ApprovalStats[]
  );
  if (total === null) throw new Error('An approval queue could not be read');
  return total;
}

/* ------------------------- Shared count (sidebar) ------------------------- */

const COUNT_MAX_AGE_MS = 5 * 60 * 1000;
const COUNT_CHECK_MS = 60 * 1000;

let latest: { count: number; at: number } | null = null;
let inFlight: Promise<void> | null = null;
/** Moves on every publish, so a read started before one cannot overwrite it. */
let generation = 0;
const listeners = new Set<(n: number) => void>();

/** Record the desk's total, computed in its default scope. */
export function publishDeskApprovalCount(n: number): void {
  generation += 1;
  latest = { count: n, at: Date.now() };
  listeners.forEach(l => l(n));
}

function refreshIfStale(token: string | undefined, userRoles: string[]): void {
  if (inFlight || document.visibilityState === 'hidden') return;
  if (latest && Date.now() - latest.at < COUNT_MAX_AGE_MS) return;
  const startedAt = generation;
  inFlight = countDeskApprovals({ token, userRoles })
    // The desk published a newer total while this read was out: keep that one.
    .then(n => {
      if (generation === startedAt) publishDeskApprovalCount(n);
    })
    // A failed read keeps the last count (or none): never a wrong 0.
    .catch(() => undefined)
    .finally(() => {
      inFlight = null;
    });
}

/**
 * The desk's latest total, or null while none is known. Reads it when there is
 * none or it is over five minutes old, and re-checks every minute while the
 * page is visible. Call only for someone who can open the desk. `ownReads`
 * false while the desk itself is open: it reads the same queues and publishes
 * its total, so reading them here too would double the load.
 */
export function useDeskApprovalCount(token?: string, ownReads = true): number | null {
  const userRoles = useUserRoles();
  const [count, setCount] = useState<number | null>(latest?.count ?? null);

  useEffect(() => {
    listeners.add(setCount);
    if (latest) setCount(latest.count);
    return () => {
      listeners.delete(setCount);
    };
  }, []);

  useEffect(() => {
    if (!ownReads) return undefined;
    refreshIfStale(token, userRoles);
    const id = window.setInterval(() => refreshIfStale(token, userRoles), COUNT_CHECK_MS);
    return () => window.clearInterval(id);
  }, [token, userRoles, ownReads]);

  return count;
}
